import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  analyticsApi,
  assessmentApi,
  ApiRequestError,
  userApi,
  type ProgressionYearDto,
} from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/ui/PageHeader";

const COLORS = ["#2E74B5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

type PersonOption = {
  id: string;
  label: string;
};

export function ProgressionPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isExpert = (user?.accessLevel ?? 0) < 50;
  const isAdmin = user?.accessProfile === "admin" || (user?.accessLevel ?? 0) >= 100;

  const [people, setPeople] = useState<PersonOption[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [targetUserId, setTargetUserId] = useState("");
  const [data, setData] = useState<ProgressionYearDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isExpert && user) {
      setTargetUserId(user.id);
      setPeople([{ id: user.id, label: user.fullName }]);
      return;
    }

    let cancelled = false;
    setPeopleLoading(true);
    void (async () => {
      try {
        let options: PersonOption[] = [];
        if (isAdmin) {
          const res = await userApi.list({ active: true });
          options = res.users
            .map((u) => ({
              id: u.id,
              label: `${u.fullName} (${u.internalId})`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, "fr"));
        } else {
          const res = await assessmentApi.list();
          const byId = new Map<string, PersonOption>();
          for (const a of res.assessments) {
            if (!byId.has(a.userId)) {
              byId.set(a.userId, {
                id: a.userId,
                label: `${a.userName} (${a.userInternalId})`,
              });
            }
          }
          options = [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, "fr"));
        }
        if (cancelled) return;
        setPeople(options);
        setTargetUserId((prev) => {
          if (prev && options.some((o) => o.id === prev)) return prev;
          return options[0]?.id ?? "";
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
          setPeople([]);
        }
      } finally {
        if (!cancelled) setPeopleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isExpert, isAdmin, user, t]);

  useEffect(() => {
    if (!targetUserId) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void analyticsApi
      .progression(targetUserId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData([]);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetUserId, t]);

  const allMPs = useMemo(
    () => [...new Set(data.flatMap((d) => Object.keys(d.byMacroProcess)))],
    [data]
  );
  const chartData = data.map((d) => ({ year: d.year, ...d.byMacroProcess }));
  const selectedLabel = people.find((p) => p.id === targetUserId)?.label;

  return (
    <div className="sm-page">
      <PageHeader title={t("individual_progression")} />
      {!isExpert ? (
        <div className="sm-card sm-card-pad" style={{ marginBottom: 12 }}>
          <label className="sm-label" htmlFor="progression-user">
            {t("select_user")}
            <select
              id="progression-user"
              className="sm-input"
              value={targetUserId}
              disabled={peopleLoading || people.length === 0}
              onChange={(e) => setTargetUserId(e.target.value)}
              style={{ display: "block", marginTop: 6, maxWidth: 480 }}
            >
              {people.length === 0 ? (
                <option value="">{peopleLoading ? t("loading") : t("no_users_found")}</option>
              ) : (
                people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))
              )}
            </select>
          </label>
          <p className="sm-muted" style={{ marginTop: 8, marginBottom: 0 }}>
            {t("progression_hint")}
          </p>
        </div>
      ) : null}
      {error ? <p className="sm-error">{error}</p> : null}
      {loading || peopleLoading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-card sm-card-pad">
          {chartData.length === 0 ? (
            <p className="sm-muted">
              {!targetUserId
                ? t("progression_select_hint")
                : t("progression_empty_hint", { name: selectedLabel ?? "—" })}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} />
                <Tooltip />
                <Legend />
                {allMPs.map((mp, i) => (
                  <Line
                    key={mp}
                    type="monotone"
                    dataKey={mp}
                    stroke={COLORS[i % COLORS.length]}
                    dot
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}
